import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withTheme } from 'styled-components';
import {
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import Icon from '@mdi/react';
import {
  mdiSortAscending,
  mdiSortDescending,
  mdiDelete,
  mdiPencil,
  mdiClipboardOutline,
  mdiCheckboxMarkedCircleOutline,
  mdiCheckboxBlankCircleOutline,
  mdiCircle,
  mdiUpload,
  mdiBookPlus,
  mdiNotePlus,
} from '@mdi/js';
import Checkbox from 'rc-checkbox';
import { tokenSelector } from '../Main/reducer';
import {
  toggleSelectAll,
  toggleSelectById,
  deleteNotes,
  updateSortBy,
  DELETE_NOTES_REQUEST,
  GET_NOTES_REQUEST,
  getNotes,
} from './actions';
import Button from '../../components/Button';
import {
  selectAllCheckedSelector,
  filteredNotesSelector,
  sortBySelector,
} from './reducer';
import { createLoadingSelector } from '../Main/reducer';
import Notes from './components/Notes';
import Note from './components/Note';
import IconButton from './components/IconButton';
import Search from '../Search';
import Dropdown from '../../components/Dropdown';
import BlockUi from 'react-block-ui';
import NoNotes from './components/NoNotes';
import { FormattedMessage } from 'react-intl';
import { Helmet } from 'react-helmet';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import ConfirmModal from './components/ConfirmModal';

const styles = `
  .action-buttons-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    margin-left: auto;
  }

  .action-button {
    display: flex;
    align-items: center;
    padding: 8px;
    min-width: 40px;
  }

  .action-button-icon {
    width: 24px !important;
    height: 24px !important;
  }

  .action-button-text {
    margin-left: 8px;
    font-size: 14px;
  }

  .delete-button-icon {
    width: 24px !important;
    height: 24px !important;
    margin-top: 0 !important;
  }

  @media (max-width: 576px) {
    .action-buttons-container {
      margin-top: 12px;
      justify-content: flex-start;
    }

    .action-button {
      padding: 6px;
    }

    .action-button-text {
      display: none;
    }
  }
`;

class NotesPage extends React.Component {
  state = {
    sortByOpen: false,
    confirmDeleteOpen: false,
    currentPage: 1,
  };

  notesPerPage = 12;

  openConfirmModal = () => {
    this.setState({ confirmDeleteOpen: true });
  };

  closeConfirmModal = () => {
    this.setState({ confirmDeleteOpen: false });
  };

  confirmDelete = () => {
    this.setState({ confirmDeleteOpen: false }, () => {
      this.props.deleteNotes();
      this.setState({ currentPage: 1 });
    });
  };

  componentDidMount() {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    if (this.props.token) {
      this.props.getNotes();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.token !== prevProps.token && this.props.token) {
      this.props.getNotes();
    }
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  renderSortBy = (sortBy) =>
    this.props.sortBy === sortBy ? (
      <Icon
        path={mdiSortAscending}
        size={0.8}
        color={this.props.theme.color.default}
        className="action-button-icon"
      />
    ) : this.props.sortBy === `-${sortBy}` ? (
      <Icon
        path={mdiSortDescending}
        size={0.8}
        color={this.props.theme.color.default}
        className="action-button-icon"
      />
    ) : (
      ''
    );

  render() {
    const {
      theme,
      history,
      filteredNotes,
      selectAllChecked,
      loading,
      toggleSelectAll,
      toggleSelectById,
      updateSortBy,
    } = this.props;
    const { sortByOpen, currentPage } = this.state;

    const totalNotes = filteredNotes ? filteredNotes.length : 0;
    const totalPages = Math.ceil(totalNotes / this.notesPerPage);
    const startIndex = (currentPage - 1) * this.notesPerPage;
    const endIndex = startIndex + this.notesPerPage;
    const currentNotes = filteredNotes
      ? filteredNotes.slice(startIndex, endIndex)
      : [];

    if (loading) {
      return (
        <BlockUi blocking={true}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'top',
              height: '500px',
              fontSize: '1.4rem',
            }}
          >
            Loading notes...
          </div>
        </BlockUi>
      );
    }

    return (
      <BlockUi
        blocking={false}
        className="d-flex flex-column flex-fill"
        style={{ marginRight: '-20px' }}
      >
        <ConfirmModal
          isOpen={this.state.confirmDeleteOpen}
          toggle={this.closeConfirmModal}
          onConfirm={this.confirmDelete}
          message={
            'Are you sure you want to delete the selected notes? Any related records will also be deleted.'
          }
        />
        <Helmet>
          <title>Secure voice notes | VoiceboxMD Web</title>
          <meta
            name="description"
            content="Create secure notes using speech to text recognition."
          />
        </Helmet>
        <Search
          className="d-flex d-sm-none mb-2 mb-md-4"
          style={{ flex: '0 0' }}
        />
        <div
          style={{ marginRight: '20px', flex: '0 0 auto' }}
          className="d-flex align-items-center flex-wrap"
        >
          <div className="mr-3 d-inline-flex align-items-center">
            <Checkbox checked={selectAllChecked} onChange={toggleSelectAll} />
            <div className="ml-1 pl-1">
              <FormattedMessage id="NotesPage.selectAll" />
            </div>
          </div>
          <Button
            onClick={this.openConfirmModal}
            noPadding
            className="mr-3 action-button"
          >
            <Icon
              path={mdiDelete}
              size={1}
              color={theme.color.default}
              className="delete-button-icon"
            />
            <div className="action-button-text">
              <FormattedMessage id="NotesPage.deleteNote" />
            </div>
          </Button>
          <Dropdown
            isOpen={sortByOpen}
            toggle={() => this.setState({ sortByOpen: !sortByOpen })}
            box="box"
          >
            <Dropdown.Toggle caret>
              <FormattedMessage id="NotesPage.sortBy" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => updateSortBy('date')}>
                <FormattedMessage id="NotesPage.date" />{' '}
                {this.renderSortBy('date')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => updateSortBy('title')}>
                <FormattedMessage id="NotesPage.title" />{' '}
                {this.renderSortBy('title')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => updateSortBy('text')}>
                <FormattedMessage id="NotesPage.text" />{' '}
                {this.renderSortBy('text')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <div className="action-buttons-container">
            <Button
              className="action-button"
              noPadding
              onClick={() => history.push('/uploadfile')}
            >
              <Icon
                path={mdiUpload}
                size={1}
                color={theme.color.action}
                className="action-button-icon"
              />
              <div className="action-button-text d-none d-sm-block">
                <FormattedMessage id="NotesPage.uploadFile" />
              </div>
            </Button>
            <Button
              className="action-button"
              noPadding
              onClick={() => history.push('/templates')}
            >
              <Icon
                path={mdiBookPlus}
                size={1}
                color={theme.color.action}
                className="action-button-icon"
              />
              <div className="action-button-text d-none d-sm-block">
                <FormattedMessage id="NotesPage.templateManager" />
              </div>
            </Button>
            <Button
              className="action-button"
              noPadding
              onClick={() => history.push('/edit')}
            >
              <Icon
                path={mdiNotePlus}
                size={1}
                color={theme.color.action}
                className="action-button-icon"
              />
              <div className="action-button-text d-none d-sm-block">
                <FormattedMessage id="NotesPage.addNote" />
              </div>
            </Button>
          </div>
        </div>
        {currentNotes && currentNotes.length > 0 && (
          <Notes className="flex-grow-1">
            <Row noGutters style={{ marginTop: '-17px' }}>
              {currentNotes.map((note) => (
                <Col xl="3" lg="4" sm="6" key={note['id']}>
                  <Note
                    selected={note.selected}
                    onClick={() => history.push(`/view/${note.id}`)}
                  >
                    <Note.Action onClick={(e) => e.stopPropagation()}>
                      <IconButton onClick={() => toggleSelectById(note.id)}>
                        <Icon
                          path={
                            note.selected
                              ? mdiCheckboxMarkedCircleOutline
                              : mdiCheckboxBlankCircleOutline
                          }
                          size={1}
                          color={theme.color.default}
                          className="action-button-icon"
                        />
                      </IconButton>
                      <CopyToClipboard
                        text={note.text}
                        onCopy={() => toast.info(`Note ${note.title} copied!`)}
                      >
                        <IconButton className="ml-2">
                          <Icon
                            path={mdiClipboardOutline}
                            size={1}
                            color={theme.color.default}
                            className="action-button-icon"
                          />
                        </IconButton>
                      </CopyToClipboard>
                      <IconButton
                        onClick={() => history.push(`/edit/${note.id}`)}
                        className="ml-2"
                      >
                        <Icon
                          path={mdiPencil}
                          size={1}
                          color={this.props.theme.color.default}
                          className="action-button-icon"
                        />
                      </IconButton>
                    </Note.Action>
                    <p>{note['date']}</p>
                    <Note.Title style={{ marginTop: '10px', height: '90px' }}>
                      <Icon
                        path={mdiCircle}
                        size={0.4}
                        color={theme.color.dot}
                        className="action-button-icon"
                      />
                      <span className="ml-2 pl-1">
                        {note['title'].length > 50
                          ? `${note['title'].slice(0, 50)}...`
                          : note['title']}
                      </span>
                    </Note.Title>
                    <Note.Text
                      className="mb-0"
                      dangerouslySetInnerHTML={{ __html: note['text'] }}
                    />
                  </Note>
                </Col>
              ))}
            </Row>
          </Notes>
        )}
        {(currentNotes == null || currentNotes.length === 0) && (
          <NoNotes>
            <img alt="no-notes" src={require('../../assets/no-notes.png')} />
            <h1>
              <FormattedMessage id="NotesPage.noNotes.title" />
            </h1>
            <div>
              <p className="mb-0">
                <FormattedMessage id="NotesPage.noNotes.desc1" />
              </p>
              <p>
                <FormattedMessage id="NotesPage.noNotes.desc2" />
              </p>
            </div>
            <Button primary onClick={() => history.push('/edit')}>
              <FormattedMessage id="NotesPage.addNote" />
            </Button>
          </NoNotes>
        )}
        {totalNotes > this.notesPerPage && (
          <Pagination
            className="mt-4 justify-content-center flex-wrap"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                previous
                onClick={() => this.handlePageChange(currentPage - 1)}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, index) => (
              <PaginationItem active={currentPage === index + 1} key={index}>
                <PaginationLink
                  onClick={() => this.handlePageChange(index + 1)}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                next
                onClick={() => this.handlePageChange(currentPage + 1)}
              />
            </PaginationItem>
          </Pagination>
        )}
      </BlockUi>
    );
  }
}

NotesPage = withTheme(NotesPage);

NotesPage = connect(
  createStructuredSelector({
    token: tokenSelector,
    selectAllChecked: selectAllCheckedSelector,
    filteredNotes: filteredNotesSelector,
    sortBy: sortBySelector,
    loading: createLoadingSelector([DELETE_NOTES_REQUEST, GET_NOTES_REQUEST]),
  }),
  { toggleSelectAll, toggleSelectById, deleteNotes, updateSortBy, getNotes }
)(NotesPage);

export default NotesPage;
